/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();

      this.buttons = this.querySelectorAll('.full-menu--link-parent');
      this.header = this.closest('.header');
      this.subButtons = this.querySelectorAll('.full-menu--link-child');
      this.panel = this.querySelector('.full-menu--panel');
      this.panel1 = this.querySelector('.full-menu--panel--level-1');
      this.panel2 = this.querySelector('.full-menu--panel--level-2');
      this.cc = this.querySelector('.full-menu--cc');
      this.toggle = this.querySelector('.full-menu--toggle');
      this.panelWidth = 320;
      this.rtl = document.dir === 'rtl';
      this.closeDelay = 300;
      this.switchDelay = 180;
    }
    connectedCallback() {
      this.buttons.forEach((button, i) => {
        button.addEventListener('mouseenter', (e) => this.scheduleHover(() => this.onParentHover(button, e, i)));
        button.addEventListener('mouseleave', () => this.cancelHover());
        button.addEventListener('keydown', (e) => this.onParentKeydown(button, e, i));
        button.addEventListener('focus', (e) => this.onParentFocus(button, e, i));
      });
      this.subButtons.forEach((button, i) => {
        button.addEventListener('mouseenter', (e) => this.scheduleHover(() => this.onChildHover(button, e, i)));
        button.addEventListener('mouseleave', () => this.cancelHover());
        button.addEventListener('focus', (e) => {
          this.cancelClose();
          this.cancelHover();
          this.onChildHover(button, e, i);
        });
        button.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' || !button.classList.contains('has-sub-menu')) return;
          e.preventDefault();
          this.onChildHover(button, e, i);
          this.panel2.querySelector(button.dataset.target)?.querySelector('a')?.focus();
        });
      });
      this.panel2.addEventListener('mouseleave', () => {
        clearTimeout(this.panelLeaveTimer);
        this.panelLeaveTimer = setTimeout(() => this.onPanelLeave(), this.closeDelay);
      });
      this.interactionAreas = [this.querySelector('.full-menu--menu'), this.panel, this.toggle];
      this.hoveredAreas = new Set();
      this.interactionAreas.forEach((area) => {
        area.addEventListener('mouseenter', () => {
          this.hoveredAreas.add(area);
          this.cancelClose();
        });
        area.addEventListener('mouseleave', () => {
          this.hoveredAreas.delete(area);
          this.scheduleClose();
        });
      });
      [this.panel1, this.panel2].forEach((area) => {
        area.addEventListener('mouseenter', () => {
          this.cancelHover();
          clearTimeout(this.panelLeaveTimer);
        });
      });
      this.addEventListener('mouseleave', () => this.scheduleClose());
      this.toggle.addEventListener('click', this.onToggleClick.bind(this));
      this.cc.addEventListener('mouseenter', () => this.scheduleClose());
      this.cc.addEventListener('click', () => this.onCC());
      this.panel.style.setProperty('--menu-panel-width-total', (this.panelWidth * 3) + 'px');

      this.addEventListener('focusin', () => {
        clearTimeout(this.focusLeaveTimer);
        this.cancelClose();
        this.cancelHover();
        clearTimeout(this.panelLeaveTimer);
      });
      this.addEventListener('focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.cancelHover();
        clearTimeout(this.focusLeaveTimer);
        // A null relatedTarget needs the browser to finish moving focus first.
        this.focusLeaveTimer = setTimeout(() => {
          // Focus leaving the navigation takes priority over a stationary pointer.
          if (!this.contains(document.activeElement)) this.onCC();
        }, 0);
      });
      document.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() === 'ESCAPE') {
          this.onCC();
        }
      });
    }
    cancelHover() {
      clearTimeout(this.hoverTimer);
    }
    cancelClose() {
      clearTimeout(this.closeTimer);
    }
    scheduleHover(callback) {
      this.cancelClose();
      this.cancelHover();
      clearTimeout(this.panelLeaveTimer);
      if (!this.header.classList.contains('open-menu')) {
        callback();
        return;
      }
      this.hoverTimer = setTimeout(callback, this.switchDelay);
    }
    scheduleClose() {
      this.cancelClose();
      this.cancelHover();
      this.closeTimer = setTimeout(() => {
        if (!this.hasActiveInteraction()) this.onCC();
      }, this.closeDelay);
    }
    hasActiveInteraction() {
      return this.contains(document.activeElement) ||
        this.hoveredAreas.size > 0;
    }
    disconnectedCallback() {
      this.cancelClose();
      this.cancelHover();
      clearTimeout(this.panelLeaveTimer);
      clearTimeout(this.focusLeaveTimer);
      this.hoveredAreas.clear();
    }
    onParentFocus(button, e, i) {
      this.cancelClose();
      this.cancelHover();
      this.onParentHover(button, e, i);
    }
    onParentKeydown(button, e, i) {
      if (e.code.toUpperCase() !== 'ENTER') return;

      if (button.classList.contains('has-sub-menu')) {
        e.preventDefault();
        let submenus = this.panel1.querySelectorAll('.full-menu--subpanel'),
          target_selector = button.dataset.target;
        this.panel.style.setProperty('--menu-panel-width', (this.panelWidth * 2) + 'px');

        [].forEach.call(submenus, function (el) {
          el.classList.remove('active');
        });
        this.panel1.querySelector(target_selector).classList.add('active');
        this.panel1.querySelector(target_selector).querySelectorAll('a')[0].focus();
      }
    }
    onParentHover(button, e, i) {
      this.header.classList.add('open-menu');
      this.clearSubpanels(this.panel2);

      if (button.classList.contains('has-sub-menu')) {
        let submenus = this.panel1.querySelectorAll('.full-menu--subpanel'),
          target_selector = button.dataset.target;
        this.panel.style.setProperty('--menu-panel-width', (this.panelWidth * 2) + 'px');

        [].forEach.call(submenus, function (el) {
          el.classList.remove('active');
        });
        this.panel1.querySelector(target_selector).classList.add('active');
      } else {
        this.clearSubpanels(this.panel1);
        this.panel.style.setProperty('--menu-panel-width', (this.panelWidth * 1) + 'px');
      }
      window.dispatchEvent(new Event('resize'));
      e.preventDefault();
    }
    onChildHover(button, e, i) {
      if (button.classList.contains('has-sub-menu')) {
        let submenus = this.panel2.querySelectorAll('.full-menu--subpanel'),
          target_selector = button.dataset.target;

        this.panel.style.setProperty('--menu-panel-width', (this.panelWidth * 3) + 'px');
        [].forEach.call(submenus, function (el) {
          el.classList.remove('active');
        });
        this.panel2.querySelector(target_selector).classList.add('active');
      } else {
        this.clearSubpanels(this.panel2);
        this.panel.style.setProperty('--menu-panel-width', (this.panelWidth * 2) + 'px');
      }

      e.preventDefault();
    }
    onPanelLeave() {
      if (this.panel2.contains(document.activeElement)) return;
      this.clearSubpanels(this.panel2);
      this.panel.style.setProperty('--menu-panel-width', (this.panelWidth * 2) + 'px');
    }
    clearSubpanels(area) {
      area.querySelectorAll('.full-menu--subpanel').forEach((panel) => {
        panel.classList.remove('active');
      });
    }
    onToggleClick() {
      this.cancelClose();
      this.header.classList.add('open-menu');
    }
    onCC() {
      clearTimeout(this.focusLeaveTimer);
      this.cancelClose();
      this.cancelHover();
      clearTimeout(this.panelLeaveTimer);
      this.panel.style.setProperty('--menu-panel-width', (this.panelWidth) + 'px');
      this.panel.querySelectorAll('.full-menu--subpanel').forEach(function (el) {
        el.classList.remove('active');
      });
      this.header.classList.remove('open-menu');
    }
  }
  customElements.define('full-menu', FullMenu);
}
